import { useQuery } from '@tanstack/react-query';

import { patientApi } from '../api/patient.adapter';
import type {
  ListPatientsParams,
  ListPatientsResult,
  Patient,
} from '../types/patient.types';

// Cache keys — `all` is the broad-invalidate root used by the mutation hooks
// after memo edit / close / register; `list(params)` keeps each filter+sort+
// page combination independently cached; `detail(id)` is the slide-panel key.
export const patientQueryKeys = {
  all: ['patients'] as const,
  list: (params: ListPatientsParams) =>
    [...patientQueryKeys.all, 'list', params] as const,
  detail: (id: number) => [...patientQueryKeys.all, 'detail', id] as const,
};

// Paginated list (PRD §3.1 / §3.2 / §3.3 / §3.5).
export function usePatientsQuery(params: ListPatientsParams) {
  return useQuery<ListPatientsResult>({
    queryKey: patientQueryKeys.list(params),
    queryFn: () => patientApi.listPatients(params),
    // Keep prior page rendered while React Query refetches on filter/sort
    // change — matches the prototype's "smooth transition" UX.
    placeholderData: (prev) => prev,
  });
}

// Single-patient detail (PRD §3.6 slide panel). The mutation hooks patch the
// cache for this key directly on success so the panel re-renders without a
// round-trip.
export function usePatientQuery(id: number | null) {
  return useQuery<Patient>({
    // Use a stable key even when disabled; React Query requires a valid key.
    queryKey: patientQueryKeys.detail(id ?? -1),
    queryFn: () => patientApi.getPatient(id as number),
    enabled: id != null && id > 0,
  });
}
